import { useState } from 'react';

import type { CustomRequest } from '../lib/piClient.js';

interface Props {
  busy: boolean;
  /** Surfaces piError or validation errors above the form. */
  errorMessage?: string | null;
  /**
   * Called when the operator clicks "Activate Custom AP". The parent
   * is responsible for calling `pi.client.custom(req)` and then driving
   * the Soluu through the standard configure-and-connect flow.
   */
  onActivate(req: CustomRequest): void;
}

const MAX_SSID_BYTES = 50;
const MAX_PASSWORD_BYTES = 50;

function utf8Bytes(s: string): number {
  return new TextEncoder().encode(s).length;
}

/**
 * Expert mode: arbitrary AP configuration outside the named scenario
 * catalog. Use when you want to test a specific band/channel/SSID/password
 * combination that isn't (and shouldn't be) a permanent scenario. Backs
 * onto the Pi service's POST /ap/custom endpoint.
 *
 * Field constraints mirror what hostapd will accept on Bookworm + brcmfmac
 * and what the Soluu §311/§312 characteristics will accept (50 bytes max).
 */
export function ExpertApForm({ busy, errorMessage = null, onActivate }: Props) {
  const [ssid, setSsid] = useState('');
  const [password, setPassword] = useState('');
  const [country, setCountry] = useState('US');
  const [channel, setChannel] = useState<number>(6);
  const [txPowerDbm, setTxPowerDbm] = useState<number>(20);
  const [ieee80211d, setIeee80211d] = useState<boolean>(false);

  const ssidBytes = utf8Bytes(ssid);
  const pwBytes = utf8Bytes(password);
  const ssidOk = ssid.trim().length > 0 && ssidBytes <= MAX_SSID_BYTES;
  const pwOk = password.length >= 8 && pwBytes <= MAX_PASSWORD_BYTES;
  const channelOk = Number.isInteger(channel) && channel >= 1 && channel <= 14;
  const txOk = Number.isInteger(txPowerDbm) && txPowerDbm >= 0 && txPowerDbm <= 30;
  const countryOk = /^[A-Z0-9]{2}$/.test(country);
  const canActivate = ssidOk && pwOk && channelOk && txOk && countryOk && !busy;

  return (
    <section className="section">
      <h2>Expert: Custom AP</h2>
      <p className="muted" style={{ marginTop: 0 }}>
        Arbitrary band / channel / SSID / password. Use for one-off tests
        (e.g. reproducing a specific real-world AP). For repeatable tests
        across sessions, add a named scenario in{' '}
        <code>packages/pi-ap-control/src/scenarios.ts</code> instead.
      </p>
      {errorMessage && (
        <div className="banner error" style={{ marginBottom: '0.75rem' }}>
          {errorMessage}
        </div>
      )}
      <div className="row" style={{ gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ flex: '0 0 6rem' }}>
          <label htmlFor="expert-country" className="muted" style={{ display: 'block', fontSize: '0.85rem' }}>
            Country
          </label>
          <input
            id="expert-country"
            type="text"
            value={country}
            onChange={(e) => setCountry(e.target.value.toUpperCase().slice(0, 2))}
            disabled={busy}
            maxLength={2}
            style={{ width: '100%', textTransform: 'uppercase' }}
          />
        </div>
        <div style={{ flex: '0 0 5rem' }}>
          <label htmlFor="expert-channel" className="muted" style={{ display: 'block', fontSize: '0.85rem' }}>
            Channel
          </label>
          <input
            id="expert-channel"
            type="number"
            min={1}
            max={14}
            value={channel}
            onChange={(e) => setChannel(Number.parseInt(e.target.value, 10) || 0)}
            disabled={busy}
            style={{ width: '100%' }}
          />
        </div>
        <div style={{ flex: '0 0 5rem' }}>
          <label htmlFor="expert-txpower" className="muted" style={{ display: 'block', fontSize: '0.85rem' }}>
            TX dBm
          </label>
          <input
            id="expert-txpower"
            type="number"
            min={0}
            max={30}
            value={txPowerDbm}
            onChange={(e) => setTxPowerDbm(Number.parseInt(e.target.value, 10) || 0)}
            disabled={busy}
            style={{ width: '100%' }}
          />
        </div>
        <div style={{ flex: '0 0 auto' }}>
          <label className="muted" style={{ display: 'block', fontSize: '0.85rem' }}>
            802.11d
          </label>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
            <input
              type="checkbox"
              checked={ieee80211d}
              onChange={(e) => setIeee80211d(e.target.checked)}
              disabled={busy}
            />
            broadcast country
          </label>
        </div>
      </div>
      <div className="row" style={{ gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end', marginTop: '0.5rem' }}>
        <div style={{ flex: '1 1 18rem' }}>
          <label htmlFor="expert-ssid" className="muted" style={{ display: 'block', fontSize: '0.85rem' }}>
            SSID ({ssidBytes}/{MAX_SSID_BYTES} bytes)
          </label>
          <input
            id="expert-ssid"
            type="text"
            value={ssid}
            onChange={(e) => setSsid(e.target.value)}
            disabled={busy}
            spellCheck={false}
            autoComplete="off"
            style={{ width: '100%' }}
          />
        </div>
        <div style={{ flex: '1 1 14rem' }}>
          <label htmlFor="expert-password" className="muted" style={{ display: 'block', fontSize: '0.85rem' }}>
            WPA passphrase ({pwBytes}/{MAX_PASSWORD_BYTES} bytes)
          </label>
          <input
            id="expert-password"
            type="text"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={busy}
            spellCheck={false}
            autoComplete="off"
            style={{ width: '100%' }}
          />
        </div>
        <button
          type="button"
          className="primary"
          onClick={() =>
            onActivate({
              ssid,
              password,
              country,
              channel,
              txPowerDbm,
              ieee80211d,
            })
          }
          disabled={!canActivate}
        >
          {busy ? 'Activating…' : 'Activate Custom AP'}
        </button>
      </div>
      {!pwOk && password.length > 0 && password.length < 8 && (
        <p className="muted" style={{ marginTop: '0.5rem' }}>
          WPA2-PSK passphrase must be at least 8 characters.
        </p>
      )}
      {!countryOk && country.length === 2 && (
        <p className="muted" style={{ marginTop: '0.5rem' }}>
          Country must be a 2-letter ISO 3166 code (US, BR, GB, …) or "00"
          for the IEEE 802.11d World domain — note hostapd rejects "00"
          even though the kernel accepts it.
        </p>
      )}
    </section>
  );
}
